/**
 * Wire types and query helpers for entry templates (V5-35).
 *
 * A template is a NAMED, USER-AUTHORED blueprint for one income or expense
 * entry. Picking one PRE-FILLS the entry form; it never saves anything by
 * itself. See `backend/api/app/services/template.py` for why templates are their
 * own concept rather than a view over `MonthlyCommitment` or over the
 * history-derived suggestions at `/py-api/v1/suggestions`.
 *
 * WHY THESE TYPES LIVE HERE AND NOT IN `lib/templates/schema.ts`
 * --------------------------------------------------------------
 * The house pattern is `lib/<domain>/schema.ts` for a domain with zod write
 * schemas. Templates validate server-side (the service owns name uniqueness,
 * the category-kind rule and the archived rule, none of which a client can
 * check), so there is no zod schema to house and the feature stays one
 * self-contained folder instead of two.
 */

/** The two entry kinds a template can describe. Matches the `type` parameter
 *  `/py-api/v1/suggestions` already uses, so the SPA speaks one word. */
export type TemplateType = "income" | "expense";

/** Resolved display echo of a referenced category. Absent when it is gone. */
export interface TemplateCategoryEcho {
  name: string;
  color: string | null;
  isArchived: boolean;
}

/** Resolved display echo of a referenced account. Absent when it is gone. */
export interface TemplateAccountEcho {
  nickname: string | null;
  bankName: string;
  color: string | null;
  isArchived: boolean;
}

/** One template, exactly as `GET /py-api/v1/templates` returns it. */
export interface EntryTemplate {
  id: string;
  name: string;
  type: TemplateType;
  /** `null` means "ask me every time" — a real case (groceries, fuel). */
  amountPaise: number | null;
  categoryId: string | null;
  accountId: string | null;
  /** Expense-only; the server clears it on an income template. */
  paymentMethod: string | null;
  description: string | null;
  isArchived: boolean;
  lastUsedAt: string | null;
  useCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  category: TemplateCategoryEcho | null;
  account: TemplateAccountEcho | null;
  /**
   * A referenced category or account is missing, deleted or archived. Advisory
   * only — a stale template still applies. The server flags rather than repairs
   * it, because silently blanking a saved `categoryId` would rewrite the user's
   * intent, which the archived-category rule forbids.
   */
  isStale: boolean;
}

/**
 * What `POST /py-api/v1/templates/{id}/use` returns: exactly the entry-form
 * fields a template fills.
 *
 * There is NO `date`. A template describes WHAT, never WHEN — the entry form
 * defaults the date to today, and a stored date would be wrong on every use
 * after the first.
 *
 * References that no longer resolve are already dropped server-side, so this
 * payload is always directly saveable.
 */
export interface TemplatePrefill {
  templateId: string;
  type: TemplateType;
  amountPaise: number | null;
  categoryId: string | null;
  accountId: string | null;
  paymentMethod: string | null;
  description: string | null;
}

/** Body for create; also the shape the edit form submits. */
export interface TemplateInput {
  name: string;
  type: TemplateType;
  amountPaise: number | null;
  categoryId: string | null;
  accountId: string | null;
  paymentMethod: string | null;
  description: string | null;
}

/**
 * Query keys. First element is the domain noun, so
 * `invalidateQueries({ queryKey: ["templates"] })` refreshes every variant —
 * the prefix-match convention the rest of the app uses.
 */
export const templateKeys = {
  all: ["templates"] as const,
  list: (type: TemplateType | "all", includeArchived: boolean) =>
    ["templates", type, includeArchived] as const,
};

/** Payment methods, mirroring `PaymentMethod` in the backend enum. */
export const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "UPI", label: "UPI" },
  { value: "CASH", label: "Cash" },
  { value: "CREDIT_CARD", label: "Credit Card" },
  { value: "DEBIT_CARD", label: "Debit Card" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "OTHER", label: "Other" },
];

/** `"tpl_abc"` -> a stable accent for its chip, so a template keeps its colour
 *  between renders without needing one stored. */
export function templateAccent(template: EntryTemplate): string | null {
  return template.category?.color ?? template.account?.color ?? null;
}

/**
 * Why a template is flagged, in words a user can act on. `null` when it is fine.
 *
 * Deliberately specific: "its category was deleted" tells you what to fix,
 * where a generic "needs attention" badge sends you hunting.
 */
export function staleReason(template: EntryTemplate): string | null {
  if (!template.isStale) return null;
  if (template.categoryId && !template.category) return "Its category was deleted";
  if (template.accountId && !template.account) return "Its account was deleted";
  if (template.category?.isArchived) return "Its category is archived";
  if (template.account?.isArchived) return "Its account is archived";
  return "Needs attention";
}
